import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  UserPlus,
  UserCheck,
  Play,
  CheckCircle,
  Calendar,
  Users,
  FileText,
  Award,
  ArrowLeft,
  Loader2,
  Clock,
  Activity,
  Target,
  TrendingUp
} from 'lucide-react';
const ICON_MAP = {
  'user-plus': UserPlus,
  'user-check': UserCheck,
  'play': Play,
  'check-circle': CheckCircle,
  'calendar': Calendar,
  'users': Users,
  'file-text': FileText,
  'award': Award
};

const STAGE_LABELS = {
  'registered': 'Registered',
  'edge_in_progress': 'EDGE In Progress',
  'edge_complete': 'EDGE Complete',
  'core_in_progress': 'CORE In Progress',
  'core_complete': 'CORE Complete',
  'booked': 'Session Booked',
  'applied': 'Application Submitted',
  'accepted': 'Accepted'
};

const STAGE_COLORS = {
  'registered': 'bg-gray-100 text-gray-700',
  'edge_in_progress': 'bg-blue-100 text-blue-700',
  'edge_complete': 'bg-blue-200 text-blue-800',
  'core_in_progress': 'bg-purple-100 text-purple-700',
  'core_complete': 'bg-purple-200 text-purple-800',
  'booked': 'bg-amber-100 text-amber-700',
  'applied': 'bg-orange-100 text-orange-700',
  'accepted': 'bg-emerald-100 text-emerald-700'
};

export default function AdminParticipantJourney() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [participant, setParticipant] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [metrics, setMetrics] = useState({});

  useEffect(() => {
    fetchJourney();
  }, [userId]);

  const fetchJourney = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/admin/participants/${userId}/journey`);
      setParticipant(response.data.participant);
      setTimeline(response.data.timeline || []);
      setMetrics(response.data.metrics || {});
    } catch (error) {
      toast.error('Failed to load participant journey');
      navigate('/admin/participants');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-500';
      case 'in_progress': return 'bg-blue-500';
      case 'scheduled': return 'bg-amber-500';
      case 'pending': return 'bg-orange-500';
      default: return 'bg-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="participant-journey">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => navigate('/admin/participants')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Participants
        </Button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Activity className="w-6 h-6 text-primary" />
              Participant Journey
            </h1>
            {participant && (
              <p className="text-muted-foreground">
                {participant.firstName} {participant.lastName} • {participant.email}
              </p>
            )}
          </div>
          {metrics.currentStage && (
            <Badge className={STAGE_COLORS[metrics.currentStage] || 'bg-gray-100'}>
              Current: {STAGE_LABELS[metrics.currentStage] || metrics.currentStage}
            </Badge>
          )}
        </div>

        {/* Metrics Cards */}
        <div className="grid md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total Events</p>
                  <p className="text-2xl font-bold">{metrics.totalEvents || 0}</p>
                </div>
                <Activity className="w-6 h-6 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Assessments</p>
                  <p className="text-2xl font-bold">
                    {metrics.assessmentsCompleted || 0}/{metrics.assessmentsStarted || 0}
                  </p>
                </div>
                <CheckCircle className="w-6 h-6 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Sessions</p>
                  <p className="text-2xl font-bold">{metrics.sessionsCompleted || 0}</p>
                </div>
                <Users className="w-6 h-6 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Days Since Signup</p>
                  <p className="text-2xl font-bold">{metrics.daysSinceRegistration || 0}</p>
                </div>
                <Clock className="w-6 h-6 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Last Activity</p>
                  <p className="text-sm font-medium">
                    {metrics.lastActivity ? formatDate(metrics.lastActivity).split(',')[0] : 'Never'}
                  </p>
                </div>
                <TrendingUp className="w-6 h-6 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Journey Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Journey Timeline
            </CardTitle>
            <CardDescription>
              Complete activity history for this participant
            </CardDescription>
          </CardHeader>
          <CardContent>
            {timeline.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Activity className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No journey events recorded yet</p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />
                
                {/* Timeline events */}
                <div className="space-y-6">
                  {timeline.map((event, index) => {
                    const IconComponent = ICON_MAP[event.icon] || Activity;
                    return (
                      <div key={index} className="relative flex gap-4 pl-4">
                        {/* Timeline dot */}
                        <div className={`w-5 h-5 rounded-full ${getStatusColor(event.status)} flex items-center justify-center z-10`}>
                          <div className="w-2 h-2 rounded-full bg-white" />
                        </div>
                        
                        {/* Event content */}
                        <div className="flex-1 pb-6 -mt-1">
                          <div className="bg-card border border-border rounded-lg p-4 hover:shadow-sm transition-shadow">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-3">
                                <div className={`p-2 rounded-lg ${
                                  event.status === 'completed' ? 'bg-emerald-100' :
                                  event.status === 'in_progress' ? 'bg-blue-100' :
                                  'bg-muted'
                                }`}>
                                  <IconComponent className={`w-4 h-4 ${
                                    event.status === 'completed' ? 'text-emerald-600' :
                                    event.status === 'in_progress' ? 'text-blue-600' :
                                    'text-muted-foreground'
                                  }`} />
                                </div>
                                <div>
                                  <h4 className="font-medium">{event.title}</h4>
                                  <p className="text-sm text-muted-foreground mt-0.5">
                                    {event.description}
                                  </p>
                                  
                                  {/* Results if available */}
                                  {event.results && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {event.results.primaryFear && (
                                        <Badge variant="outline" className="text-xs">
                                          Fear: {event.results.primaryFear}
                                        </Badge>
                                      )}
                                      {event.results.primaryArchetype && (
                                        <Badge variant="outline" className="text-xs">
                                          Archetype: {event.results.primaryArchetype}
                                        </Badge>
                                      )}
                                      {event.results.intensity && (
                                        <Badge variant="secondary" className="text-xs">
                                          {event.results.intensity} Intensity
                                        </Badge>
                                      )}
                                      {event.results.score && (
                                        <Badge variant="secondary" className="text-xs">
                                          Score: {event.results.score}
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Duration if available */}
                                  {event.duration && (
                                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      Duration: {event.duration}
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              <div className="text-right flex-shrink-0">
                                <Badge 
                                  variant={event.status === 'completed' ? 'default' : 'secondary'}
                                  className={event.status === 'completed' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}
                                >
                                  {event.status}
                                </Badge>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {formatDate(event.timestamp)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={() => navigate(`/admin/participants/${userId}`)}>
            View Full Profile
          </Button>
          <Button variant="outline" onClick={() => window.print()}>
            Print Timeline
          </Button>
        </div>
      </div>
  );
}
